import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserSuggestions } from '../../common/use-user-suggestions';
import {
  isOpenProfileShortcut,
  useOpenMentionProfile,
} from '../mention-shared/mention-menu-item';
import {
  useMentionComboboxAria,
  useMentionListboxId,
} from '../mention-shared/use-mention-combobox-aria';
import MentionPickerList from './MentionPickerList';
import './mention-picker.scss';

type MentionPickerProps = {
  matchString: string;
  onClick: (mention: string, advocateId?: string) => void;
  /** Dismisses the picker without inserting (Esc). */
  onClose?: () => void;
};

export default function MentionPicker({
  matchString,
  onClick,
  onClose,
}: MentionPickerProps) {
  const { t } = useTranslation();

  const searchText = matchString.replace('@', '');
  const users = useUserSuggestions(searchText);

  const [cursor, setCursor] = useState<number>();
  const listboxId = useMentionListboxId();
  const openProfile = useOpenMentionProfile();

  useEffect(() => {
    setCursor(users.length ? users.length - 1 : undefined);
  }, [users]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Nothing to act on (no results yet, or only the "lookup" hint is
      // showing): leave Tab/Enter alone so focus is never trapped in the form.
      if (!users.length) return;
      const cursorState = cursor || 0;
      if (e.key === 'Tab' || e.key === 'Enter') {
        const selectedUser = users[cursorState];
        if (!selectedUser) return;
        e.preventDefault();
        if (isOpenProfileShortcut(e)) {
          // The profile link is not a Tab stop; Alt+Enter is its keyboard path.
          openProfile(selectedUser.id);
          return;
        }
        onClick(`@${selectedUser.display_name}`, selectedUser.id);
      } else if (e.key === 'ArrowUp') {
        setCursor((cursorState - 1 + users.length) % users.length);
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        setCursor((cursorState + 1) % users.length);
        e.preventDefault();
      } else if (e.key === 'Escape' && onClose) {
        e.preventDefault();
        onClose();
      }
    },
    [cursor, onClick, onClose, openProfile, users]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const shouldShowMentions = matchString.length !== 1;

  // Focus stays in the comment box while the list is open, so the combobox state
  // belongs on the focused field (ARIA 1.2 editable combobox).
  const selectedUser = cursor !== undefined ? users[cursor] : undefined;
  useMentionComboboxAria(
    () => document.activeElement as HTMLElement | null,
    shouldShowMentions && users.length > 0,
    listboxId,
    selectedUser ? `${listboxId}-option-${selectedUser.id}` : undefined
  );

  return (
    <div className="mention-picker">
      {shouldShowMentions ? (
        users.length ? (
          <MentionPickerList
            users={users}
            listboxId={listboxId}
            selected={cursor}
            onClick={onClick}
            onHover={setCursor}
          />
        ) : null
      ) : (
        <div className="title">{t('comments.mentions.lookup')}</div>
      )}
    </div>
  );
}
